import {
  ALL_FIELDS,
  FEE_MODE_DEFAULTS,
  LEGACY_KEY_MAP,
} from "./Add_Loan.constants";
import { FromEpochFormat } from "../../../../../../Utils/Helpers/dateUtils";
import { v5 as uuidv5 } from "uuid";
import {
  httpDelete,
  httpGet,
  httpPost,
  httpPut,
} from "../../../../../../Utils/apis/apis";

const ADD_LOAN_DRAFT_STORAGE_PREFIX = "minerva:add-loan:draft-form-id";
const FORM_DRAFT_NAMESPACE = "b7f10a2d-6f5d-4f44-9c44-3b4f44d8a101";
const FORM_DRAFT_ENDPOINT = `${process.env.API_URL || ""}`
  .replace(/\/+$/, "")
  .toLowerCase()
  .endsWith("/api")
  ? "/formdraft"
  : "/api/formdraft";

const hasOwnValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== "" && value !== null && value !== undefined;
};

export const buildInitialState = () =>
  ALL_FIELDS.reduce((state, field) => {
    state[field.backendKey] =
      field.defaultValue !== undefined
        ? field.defaultValue
        : field.type === "file"
        ? []
        : "";
    return state;
  }, {});

export const getRecordSource = (formConfig) =>
  formConfig?.data ||
  formConfig?.record ||
  formConfig?.recordData ||
  formConfig?.properties ||
  formConfig?.templateData ||
  formConfig?.LoanDetails?.[0] ||
  formConfig[0] ||
  formConfig ||
  {};

const mapLabelToOptionValue = (label, options = []) => {
  if (!label || !Array.isArray(options)) return label;
  const match = options.find((opt) => `${opt.label}` === `${label}`);
  return match ? match.value : label;
};

export const buildFormStateFromSource = (source, optionSetData = {}) =>
  ALL_FIELDS.reduce((state, field) => {
    const legacyKey = LEGACY_KEY_MAP[field.backendKey];
    const sourceValue = source?.[field.backendKey] ?? source?.[legacyKey];

    let value = sourceValue;
    if (hasOwnValue(sourceValue)) {
      if (
        (field.type === "date" || field.type === "datetime") &&
        typeof sourceValue === "number"
      ) {
        value = FromEpochFormat(sourceValue);
      } else if (field.type === "optionset") {
        const options = optionSetData[field.backendKey] || [];
        value = mapLabelToOptionValue(sourceValue, options);
      }
    } else {
      value =
        field.defaultValue !== undefined
          ? field.defaultValue
          : field.type === "file"
          ? []
          : "";
    }

    state[field.backendKey] = value;
    return state;
  }, {});

export const isNumericType = (type) =>
  type === "int" ||
  type === "float" ||
  type === "integer" ||
  type === "decimal";

export const isFieldRequired = (field, formData = {}, feeModes = {}) => {
  if (!field || field.requiredFlag !== "Y") {
    return false;
  }

  if (field.feeModeGroup && field.feeModeValue) {
    return feeModes?.[field.feeModeGroup] === field.feeModeValue;
  }

  return shouldShowField(field, formData);
};

export const shouldShowField = (field, formData = {}) => {
  if (!field) {
    return false;
  }

  if (field.visibleWhen && !field.visibleWhen(formData || {})) {
    return false;
  }

  if (field.feeModeGroup && field.feeModeValue) {
    return true;
  }

  return true;
};

export const getFieldHelperText = (field) => field.helperText || "";

export const getFieldOptions = (field, currentValue, optionSetData = {}) => {
  const baseOptions =
    field.fixedOptions || optionSetData[field.backendKey] || [];

  if (
    hasOwnValue(currentValue) &&
    !baseOptions.some((option) => `${option.value}` === `${currentValue}`)
  ) {
    return [...baseOptions, { label: `${currentValue}`, value: currentValue }];
  }

  return baseOptions;
};

export const hasFieldValue = hasOwnValue;

export const getFieldGroups = (fields) => {
  const groupMap = new Map();

  fields.forEach((field) => {
    const groupKey = field.subSection || "";

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }

    groupMap.get(groupKey).push(field);
  });

  return Array.from(groupMap.entries()).map(([title, items]) => ({
    title,
    items,
  }));
};

export const loadOptionSets = async () => {
  const optionSetCategories =
    "BusinessDevelopmentManager,LendingDirector,RelationshipManager,PortfolioManager,ProductType, LoanType, LoanPurpose, DevelopmentType, UnitType, LoanSource, SecurityTenureType, CustomerType, Currency, DrawdownDistributionType, RateType, DayCountBasis, InterestPaymentMethod, ArrangementFeePaymentMethod, ExitFeePaymentMethod, ExitStrategy, MonitoringFeePaymentMethod, BrokerFeePaymentMethod, ValuationFeePaymentMethod, LenderFeePaymentMethod, ExtensionFeePaymentMethod, RepaymentProfile, Region";
  const spResponse = await httpPost(
    `/SQLQuery/getDataFromMultipleSPs?chartSPs=false`,
    [
      {
        id: "AllOptionSets",
        spName: "[DF].[usp_GetOptionSetsByCategories]",
        parameters: {
          Categories: optionSetCategories,
        },
      },
    ],
    {},
    true
  );
  const data = spResponse?.data?.AllOptionSets || [];
  const categories = optionSetCategories
    .split(",")
    .map((category) => category.trim());

  return categories.reduce((accumulator, category) => {
    const items = data
      .filter((item) => item.category === category)
      .map(({ id, label, value }) => ({
        Id: id,
        label,
        value,
      }));

    accumulator[category] = items.length ? items : null;
    return accumulator;
  }, {});
};

export const loadBorrowerLookupOptions = async () => {
  const spResponse = await httpPost(
    `/SQLQuery/getDataFromMultipleSPs?chartSPs=false`,
    [
      {
        id: "BorrowerLookup",
        spName: "DF.usp_FormLookupField",
        parameters: {
          lookupName: "Borrower",
        },
      },
    ],
    {},
    true
  );
  const rows = spResponse?.data?.BorrowerLookup || [];
  return rows
    .map((item) => {
      const borrowerName = item?.label;
      const borrowerId = item?.id;
      if (!borrowerName) {
        return null;
      }
      return {
        label: borrowerName,
        value: borrowerName,
        borrowerId,
      };
    })
    .filter(Boolean);
};

const normalizeDraftIdentifier = (value = "anonymous") => {
  const normalizedValue = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalizedValue || "anonymous";
};

const resolveLoanDraftFormId = (draftIdentifier) =>
  draftIdentifier
    ? uuidv5(
        `${ADD_LOAN_DRAFT_STORAGE_PREFIX}:${draftIdentifier}`,
        FORM_DRAFT_NAMESPACE
      )
    : null;

export const getLoanDraftStorageKey = (userIdentifier = "anonymous") => {
  const hostName =
    typeof window !== "undefined"
      ? window.location?.hostname || "portal"
      : "portal";

  return [
    ADD_LOAN_DRAFT_STORAGE_PREFIX,
    normalizeDraftIdentifier(hostName),
    normalizeDraftIdentifier(userIdentifier),
  ].join(":");
};

export const hasDraftableLoanData = (
  formData = {},
  feeModes = {},
  activeStep = 0
) => {
  const initialState = buildInitialState();
  const hasFormValues = ALL_FIELDS.some((field) => {
    const currentValue = formData[field.backendKey];
    const initialValue = initialState[field.backendKey];

    if (Array.isArray(currentValue) || Array.isArray(initialValue)) {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      const initialArray = Array.isArray(initialValue) ? initialValue : [];

      if (currentArray.length !== initialArray.length) {
        return true;
      }

      return currentArray.some(
        (value, index) => `${value}` !== `${initialArray[index]}`
      );
    }

    return `${currentValue ?? ""}` !== `${initialValue ?? ""}`;
  });
  const hasNonDefaultFeeModes = Object.keys(feeModes).some(
    (group) => feeModes[group] && feeModes[group] !== "rate"
  );

  return hasFormValues || hasNonDefaultFeeModes || activeStep > 0;
};

export const saveLoanDraft = async (storageKey, payload) => {
  const formId = resolveLoanDraftFormId(storageKey);

  if (!formId) {
    return false;
  }

  try {
    let response = await httpPut(
      `${FORM_DRAFT_ENDPOINT}/${encodeURIComponent(formId)}`,
      {
        formId,
        content: JSON.stringify(payload),
      }
    );

    if (response?.response?.status === 404 || response?.status === 404) {
      response = await httpPost(FORM_DRAFT_ENDPOINT, {
        formId,
        content: JSON.stringify(payload),
      });
    }

    if (
      response?.response?.status >= 400 ||
      response?.status >= 400 ||
      response?.isAxiosError
    ) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const loadLoanDraft = async (storageKey) => {
  const formId = resolveLoanDraftFormId(storageKey);

  if (!formId) {
    return null;
  }

  try {
    const response = await httpGet(
      `${FORM_DRAFT_ENDPOINT}?formId=${encodeURIComponent(formId)}`
    );

    if (response?.response?.status === 404 || response?.status === 404) {
      return null;
    }

    if (!response?.content) {
      return null;
    }

    return JSON.parse(response.content);
  } catch (error) {
    return null;
  }
};

export const clearLoanDraft = async (storageKey) => {
  const formId = resolveLoanDraftFormId(storageKey);

  if (!formId) {
    return;
  }

  try {
    await httpDelete(`${FORM_DRAFT_ENDPOINT}/${encodeURIComponent(formId)}`);
  } catch (error) {
    // ignore API delete failure so the form can still be reset in the UI
  }
};

export const sanitizeLoanDraft = (draft) => {
  if (!draft || typeof draft !== "object") {
    return null;
  }

  const nextFormData = buildInitialState();

  ALL_FIELDS.forEach((field) => {
    const value = draft?.formData?.[field.backendKey];

    nextFormData[field.backendKey] = hasOwnValue(value)
      ? value
      : field.defaultValue !== undefined
      ? field.defaultValue
      : field.type === "file"
      ? []
      : "";
  });

  return {
    activeStep:
      typeof draft.activeStep === "number" && draft.activeStep >= 0
        ? draft.activeStep
        : 0,
    feeModes: {
      ...draft.feeModes,
    },
    formData: nextFormData,
    borrowerSelection:
      draft?.borrowerSelection && typeof draft.borrowerSelection === "object"
        ? {
            borrowerId: draft.borrowerSelection.borrowerId || "",
            label: draft.borrowerSelection.label || "",
            value: draft.borrowerSelection.value || "",
          }
        : null,
    updatedAt: draft.updatedAt || null,
  };
};

const pickPreferredOption = (options = [], patterns = []) => {
  if (!options || !Array.isArray(options)) {
    return null;
  }
  const normalizedPatterns = patterns.map((pattern) => pattern.toLowerCase());
  const preferredOption = options.find((option) => {
    const haystack = `${option?.label || ""} ${
      option?.value || ""
    }`.toLowerCase();
    return normalizedPatterns.some((pattern) => haystack.includes(pattern));
  });

  return preferredOption || options[0] || null;
};

const buildSampleNumber = (field) => {
  const numericSamples = {
    LoanTerm: 18,
    SVR: 6.25,
    FixedRate: 8.5,
    MarginRate: 3.75,
    ArrangementFeeRate: 2,
    ArrangementFeeAmount: 50000,
    CommitmentFeeAmount: 12000,
    ExitFeeRate: 1.5,
    ExitFeeAmount: 30000,
    BrokerFeeRate: 1,
    BrokerFeeAmount: 12000,
    ValuationFeeRate: 0.5,
    ValuationFeeAmount: 7500,
    LenderFeeRate: 0.75,
    LenderFeeAmount: 9000,
    ExtensionFeeRate: 0.5,
    ExtensionFeeAmount: 8000,
    PurchasePrice: 3500000,
    AmountSpentSincePurchase: 250000,
    StampDutyPercent: 5,
    StampDutyAmount: 175000,
    DemoAndInfrastructure: 150000,
    EquitySpentByBorrowerOnConstruction: 300000,
    MainBuild: 2200000,
    ContingencyPercent: 10,
    ContingencyAmount: 220000,
    ProfessionalFees: 180000,
    OtherFees: 45000,
    UtilityFees: 30000,
    NewBuildWarranty: 15000,
    CommunityInfrastructureLevyFees: 85000,
    BankIMS: 22000,
    MarketingFees: 55000,
  };

  const base = numericSamples[field.backendKey] || 1000;

  const variation = base * 0.1;
  const randomOffset = Math.random() * variation * 2 - variation;

  if (field.type === "integer" || field.type === "int") {
    return Math.max(1, Math.round(base + randomOffset));
  }

  return Number((base + randomOffset).toFixed(2));
};

const getUniqueLoanName = () => {
  const random = Math.random().toString(36).substring(2, 8);
  return `Developer Test Loan ${random}`;
};

const buildSampleText = (field) => {
  const textSamples = {
    LoanName: getUniqueLoanName(),
    KeyPrincipalName: "Alex Carter",
    ExposureGroupName: "AC Residential Group",
    AccountReferenceNumber: "ACC-2026-001284",
    AddressLine1: "12 Orchard Lane",
    AddressLine2: "Didsbury",
    City: "Manchester",
    Country: "United Kingdom",
    Postcode: "M1 4AB",
    Region: "North West",
    LeadSourceDetail: "Developer autofill sample",
    ExtensionOptions: "One 6 month extension available subject to approval.",
    BrokerNotes: "Auto-populated sample notes for developer testing.",
  };

  return textSamples[field.backendKey] || `${field.label} Sample`;
};

export const buildDeveloperLoanSampleData = (
  currentFormData = {},
  optionSetData = {},
  feeModes = {},
  borrowerOptions = []
) => {
  const nextFormData = {
    ...buildInitialState(),
    ...currentFormData,
  };
  const nextFeeModes = {
    ...FEE_MODE_DEFAULTS,
    ...feeModes,
  };

  const fieldByKey = ALL_FIELDS.reduce((accumulator, field) => {
    accumulator[field.backendKey] = field;
    return accumulator;
  }, {});

  const setFieldValue = (backendKey, value) => {
    nextFormData[backendKey] = value;
  };

  setFieldValue(
    "ProductType",
    pickPreferredOption(optionSetData.ProductType, ["development"])?.value || ""
  );
  setFieldValue(
    "RateType",
    pickPreferredOption(optionSetData.RateType, ["fixed"])?.value ||
      optionSetData.RateType?.[0]?.value ||
      ""
  );
  setFieldValue(
    "BorrowerName",
    pickPreferredOption(borrowerOptions, [])?.label ||
      pickPreferredOption(borrowerOptions, [])?.value ||
      ""
  );

  Object.keys(nextFeeModes).forEach((group) => {
    nextFeeModes[group] = "rate";
  });

  ALL_FIELDS.forEach((field) => {
    if (!field.showOnForm) {
      return;
    }

    if (!shouldShowField(field, nextFormData, nextFeeModes)) {
      return;
    }

    if (field.type === "optionset" || field.type === "lookup") {
      const options = getFieldOptions(
        field,
        nextFormData[field.backendKey],
        optionSetData
      );
      if (!options?.length) {
        return;
      }

      const preferredPatterns = {
        LoanPurpose: ["purchase", "refinance"],
        LoanSource: ["broker", "direct"],
        FacilityType: ["standard"],
        SecurityTenureType: ["freehold", "leasehold"],
        CustomerType: ["company", "corporate"],
        DrawdownDistributionType: ["monthly", "staged"],
        DayCountBasis: ["365", "actual"],
        InterestPaymentMethod: ["retained", "serviced"],
        ArrangementFeePaymentMethod: ["deducted", "retained"],
        ExitStrategy: ["sale", "refinance"],
        BrokerFeePaymentMethod: ["deducted", "retained"],
        ValuationFeePaymentMethod: ["deducted", "retained"],
        LenderFeePaymentMethod: ["deducted", "retained"],
        RepaymentProfile: ["bullet", "interest only"],
      };

      const selectedOption = pickPreferredOption(
        options,
        preferredPatterns[field.backendKey] || []
      );

      setFieldValue(field.backendKey, selectedOption?.value || "");
      return;
    }

    if (field.type === "date" || field.type === "datetime") {
      const dateOffsets = {
        ProjectStartDate: 14,
        BuildStartDate: 30,
        PracticalCompletionDate: 365,
      };
      const baseDate = new Date();
      const nextDate = new Date(baseDate);
      nextDate.setDate(
        baseDate.getDate() + (dateOffsets[field.backendKey] || 21)
      );
      setFieldValue(field.backendKey, Math.floor(nextDate.getTime() / 1000));
      return;
    }

    if (isNumericType(field.type)) {
      setFieldValue(field.backendKey, buildSampleNumber(field));
      return;
    }

    if (field.type === "boolean") {
      setFieldValue(field.backendKey, true);
      return;
    }

    setFieldValue(field.backendKey, buildSampleText(field));
  });

  const hiddenFieldKeys = ALL_FIELDS.filter(
    (field) => !shouldShowField(field, nextFormData, nextFeeModes)
  ).map((field) => field.backendKey);

  hiddenFieldKeys.forEach((backendKey) => {
    const field = fieldByKey[backendKey];
    nextFormData[backendKey] = field?.type === "file" ? [] : "";
  });

  return {
    formData: nextFormData,
    feeModes: nextFeeModes,
  };
};
